import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight, Check } from "lucide-react";
import { useT } from "../i18n/I18nProvider";

/**
 * The homepage's customer-first hero, extracted so it can be embedded
 * verbatim elsewhere (the homepage's own walkthrough, step 1: "this is
 * the real homepage you're on") without duplicating markup. Home.jsx below
 * calls this instead of inlining it; markup/behavior unchanged.
 *
 * Rebuilt 2026-08-27 around one behavioral goal, not a features pitch:
 * make checking a booking before paying or travelling feel as normal as
 * checking a flight ticket before the airport. See the homepage brief for
 * the full section-by-section reasoning: this component covers sections
 * 1 (hero copy) and 2 (the chat-vs-record contrast visual).
 */
export default function HomeHero() {
  const { t } = useT();
  return (
    <section className="relative overflow-hidden bg-grid">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-12 sm:pb-16 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-medium" data-testid="home-customer-badge">
            <ShieldCheck className="w-3.5 h-3.5" /> {t("home.customer.badge")}
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight text-slate-900 leading-[1.05]">
            {t("home.hero.title.a")} <span className="text-[#1B4332]">{t("home.hero.title.b")}</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
            {t("home.hero.body1")}
          </p>
          <p className="mt-2 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
            {t("home.hero.body2.pre")}<strong className="font-semibold text-slate-800">{t("home.hero.body2.word")}</strong>{t("home.hero.body2.post")}
          </p>
          <p className="mt-4 text-base sm:text-lg font-semibold text-slate-900 leading-relaxed max-w-2xl">
            {t("home.hero.lead")}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/find-booking" data-testid="home-cta-check-booking" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg btn-brand font-semibold">
              {t("home.cta.check_booking")}
            </Link>
            <Link to="/request-confirmation" data-testid="home-cta-get-in-writing" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 font-medium">
              {t("home.cta.get_in_writing")}
            </Link>
          </div>
          <p className="mt-2 text-xs text-slate-500" data-testid="home-cta-check-booking-helper">
            {t("home.hero.cta.primary.helper")}
          </p>

          <p className="mt-5 text-sm text-slate-600" data-testid="home-discover-blurb">
            {t("home.cta.still_looking")}{" "}
            <Link to="/rentals" data-testid="home-cta-discover-rentals" className="font-medium text-[#1B4332] hover:underline inline-flex items-center gap-1">
              {t("home.cta.discover")} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </p>

          <p className="mt-5 text-xs text-slate-500 leading-relaxed max-w-xl" data-testid="home-customer-trust">
            {t("home.hero.compat")}
          </p>
        </div>
        <div className="lg:col-span-5 fade-up">
          <ChatVsRecordVisual t={t} />
        </div>
      </div>
    </section>
  );
}

/**
 * The hero visual, replacing the previous abstract "route to a checkmark
 * card" illustration. Section 2 of the homepage brief: show an informal
 * chat next to the structured record it produces, and let the visitor
 * conclude the record is obviously the thing to trust: self-persuasion
 * rather than a feature claim. Entirely local demo content (no API call,
 * matches the same "sandboxed preview" convention used by the interactive
 * walkthrough further down this page).
 */
function ChatVsRecordVisual({ t }) {
  return (
    <div>
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="home-hero-visual">
        {/* Chat mockup */}
        <div className="p-5 sm:p-6 bg-[#EFEAE2]">
          <div className="flex justify-end">
            <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-white px-3.5 py-2 text-sm shadow-sm">
              {t("home.hero.visual.chat.customer")}
            </div>
          </div>
          <div className="mt-2 flex justify-start">
            <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-emerald-600 text-white px-3.5 py-2 text-sm shadow-sm">
              {t("home.hero.visual.chat.business")}
            </div>
          </div>
        </div>

        {/* Transition */}
        <div className="px-5 sm:px-6 py-3 text-center text-xs sm:text-sm font-medium text-slate-500 bg-white border-y border-dashed border-slate-200">
          {t("home.hero.visual.transition")}
        </div>

        {/* Structured record */}
        <div className="p-5 sm:p-6 bg-white">
          <div className="flex items-center gap-2 text-emerald-800">
            <div className="w-6 h-6 rounded-full bg-emerald-100 grid place-items-center flex-shrink-0">
              <Check className="w-3.5 h-3.5" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-[0.14em]">{t("home.hero.visual.record.badge")}</span>
          </div>

          <div className="mt-3 font-display text-lg font-semibold text-slate-900">
            {t("home.hero.visual.record.vehicle")}
          </div>
          <div className="text-sm text-slate-600">{t("home.hero.visual.record.dates")}</div>

          <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
            <RecordField label={t("home.hero.visual.record.pickup.label")} value={t("home.hero.visual.record.pickup.value")} />
            <RecordField label={t("home.hero.visual.record.price.label")} value={t("home.hero.visual.record.price.value")} />
            <RecordField label={t("home.hero.visual.record.deposit.label")} value={t("home.hero.visual.record.deposit.value")} />
            <RecordField label={t("home.hero.visual.record.ref.label")} value={t("home.hero.visual.record.ref.value")} mono />
          </dl>

          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-1.5 text-sm font-medium text-emerald-800">
            <Check className="w-4 h-4" /> {t("home.hero.visual.record.confirmed")}
          </div>
        </div>
      </div>

      <p className="mt-4 text-center text-sm sm:text-base text-slate-700 leading-relaxed max-w-md mx-auto">
        {t("home.hero.visual.payoff.a")} <span className="font-semibold text-slate-900">{t("home.hero.visual.payoff.b")}</span>
      </p>
    </div>
  );
}

function RecordField({ label, value, mono = false }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</dt>
      <dd className={`mt-0.5 text-slate-900 ${mono ? "font-mono text-xs" : ""}`}>{value}</dd>
    </div>
  );
}
